import React, { useMemo } from 'react'
import { Box, Tooltip, Typography } from '@mui/material'
import { C, TYPE } from '../../merchants/shared/tokens'

const cellSx = {
  border: `1px solid ${C.lineSoft}`, px: 0.75, py: 0.6, fontSize: 12, textAlign: 'center', whiteSpace: 'nowrap',
  fontVariantNumeric: 'tabular-nums', color: C.inkBody, minWidth: 48,
}
const headSx = { ...cellSx, bgcolor: C.surfaceAlt, color: C.inkMuted, fontWeight: 700, fontSize: 11.5 }

/** Shade of a cell by its MAX TAT within the grid's range: light = short, deeper = long. The number is always shown. */
const shade = (max, lo, hi) => {
  if (hi <= lo) return 'transparent'
  const share = (max - lo) / (hi - lo)
  return `rgba(67, 56, 202, ${(0.04 + share * 0.22).toFixed(3)})`
}

/** Source tags present in the grid, as a sentence. */
const legend = (grid) => {
  const has = new Set((grid?.cells || []).map((c) => c.source))
  return [
    has.has('C') && 'C = this merchant’s custom TAT (outlined)',
    has.has('P') && `P = ${grid?.courier || grid?.basisCourier || 'courier'} TAT (outlined in blue)`,
    'S = Prozo standard',
  ].filter(Boolean).join(', ') + '.'
}

/**
 * What a marked cell is compared with, for its tooltip. A custom lane: its base (the standard or the merchant's basis
 * courier). A courier lane: the standard on the courier's own grid; on a merchant grid the base under it is that
 * courier's own value, so it only says where the value comes from.
 */
const compared = (grid, c) => {
  if (c.source === 'C') return `, ${c.baseSource === 'P' ? `${grid?.basisCourier || 'courier'} TAT` : 'standard'} ${c.standardMin}-${c.standardMax}`
  if (c.source === 'P') return c.baseSource === 'P' ? `, from the ${grid?.basisCourier || 'courier'} TAT` : `, standard ${c.standardMin}-${c.standardMax}`
  return ''
}

/**
 * PTL TAT by lane as a pickup x drop zone matrix of MIN-MAX working days (GET tat/grid). Rows are pickup zones, columns
 * drop zones, both with their zone names (area on hover). The standard alone, or a merchant's / courier's effective TAT
 * where custom lanes (C) and courier lanes (P) are marked and the standard lanes (S) are not.
 *
 * @param showSource  merchant / courier view: tag cells S / P / C and outline custom (C) and courier (P) lanes
 */
export default function TatMatrix({ grid, showSource }) {
  const zones = grid?.zones || []
  const byLane = useMemo(() => Object.fromEntries((grid?.cells || []).map((c) => [c.zone, c])), [grid])
  const maxes = (grid?.cells || []).map((c) => c.maxTat)
  const lo = Math.min(...maxes)
  const hi = Math.max(...maxes)

  return (
    <Box>
      {/* 17 columns: the matrix scrolls inside its card, never the page. */}
      <Box sx={{ overflowX: 'auto' }}>
        <Box component='table' aria-label='TAT by lane, MIN-MAX working days. Rows: pickup zone. Columns: drop zone.' sx={{ borderCollapse: 'collapse', minWidth: '100%' }}>
          <thead>
            <tr>
              <Box component='th' scope='col' sx={{ ...headSx, position: 'sticky', left: 0, zIndex: 1, textAlign: 'left' }}>Pickup \ Drop</Box>
              {zones.map((z) => (
                <Box component='th' scope='col' key={z.id} sx={headSx}>
                  <Tooltip title={`${z.id} · ${z.area || ''}`}><span>{z.name || z.id}</span></Tooltip>
                </Box>
              ))}
            </tr>
          </thead>
          <tbody>
            {zones.map((p) => (
              <tr key={p.id}>
                <Box component='th' scope='row' sx={{ ...headSx, textAlign: 'left', position: 'sticky', left: 0, zIndex: 1 }}>
                  <Tooltip title={`${p.id} · ${p.area || ''}`}><span>{p.name || p.id}</span></Tooltip>
                </Box>
                {zones.map((d) => {
                  const c = byLane[`${p.id}${d.id}`]
                  if (!c) return <Box component='td' key={d.id} sx={{ ...cellSx, color: C.inkGhost }}>—</Box>
                  const custom = showSource && c.source === 'C'
                  const courier = showSource && c.source === 'P'
                  return (
                    <Tooltip key={d.id} arrow
                      title={`${p.name} to ${d.name} (${c.zone}): ${c.minTat}-${c.maxTat} working days${showSource ? compared(grid, c) : ''}`}>
                      <Box component='td' sx={{
                        ...cellSx,
                        bgcolor: custom ? C.warnSoft : courier ? C.accentSoft : shade(c.maxTat, lo, hi),
                        ...(custom && { color: C.warn, fontWeight: 700, boxShadow: `inset 0 0 0 1px ${C.warnBorder}` }),
                        ...(courier && { color: C.accent, fontWeight: 700, boxShadow: `inset 0 0 0 1px ${C.accentBorder}` }),
                      }}>
                        {c.minTat}-{c.maxTat}
                        {showSource && <Box component='span' sx={{ ml: 0.5, fontSize: 9.5, fontWeight: 700, color: custom ? C.warn : courier ? C.accent : C.inkGhost }}>{c.source}</Box>}
                      </Box>
                    </Tooltip>
                  )
                })}
              </tr>
            ))}
          </tbody>
        </Box>
      </Box>
      <Typography sx={{ ...TYPE.rowHelp, mt: 1.25 }}>
        MIN-MAX working days, Monday to Saturday. EDD uses the MAX. Deeper shade = longer MAX ({lo}-{hi} days).
        {showSource && ` ${legend(grid)}`}
      </Typography>
    </Box>
  )
}
